import { useEffect, useState } from 'react';
import { api, ManualReadingRecord, MeterSearchMatch, TranslationStep, type AttributeCheckResult, type PendingReadingChecks } from '../../api/client';
import { useFieldRole } from '../../hooks/useFieldRole';
import { translateForOr } from '../../lib/translateForOr';
import { parseDbTimestamp } from '../../lib/parseDbTimestamp';
import { Badge } from '../../components/ui';
import { DownloadOriginalButton } from '../../components/DownloadOriginalButton';
import { ReadingChecksNotice, confirmLabel, needsConfirmation } from './readingChecks';

type ApproveOpts = { asset_id?: string; realm?: string; asset_type?: string };

/** The 409 billing-api answers when a reading is held by its checks. */
function heldChecks(err: unknown): { message: string; checks: AttributeCheckResult[]; digest?: string } | null {
  const e = err as {
    status?: number;
    body?: { error?: string; message?: string; checks?: AttributeCheckResult[]; checks_digest?: string };
  };
  if (e?.status === 409 && e.body?.error === 'reading_checks' && Array.isArray(e.body.checks)) {
    return { message: e.body.message ?? 'Held for review', checks: e.body.checks, digest: e.body.checks_digest };
  }
  return null;
}

/** The fingerprint to confirm with, when the row is held. */
function confirmDigest(result: PendingReadingChecks | undefined): string | undefined {
  return result?.status === 'checked' && result.held ? result.checks_digest : undefined;
}

/** The original upload's name, for the download's fallback filename. */
function sourceFilename(row: ManualReadingRecord): string | undefined {
  const raw = row.source_payload;
  if (!raw) return undefined;
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>);
    const name = (obj as { source_filename?: unknown }).source_filename;
    return typeof name === 'string' ? name : undefined;
  } catch {
    return undefined;
  }
}

function readingsOf(row: ManualReadingRecord): Record<string, number> {
  const v = row.values_json;
  if (typeof v === 'string') {
    try { return JSON.parse(v) as Record<string, number>; } catch { return {}; }
  }
  return v || {};
}

interface SerialCheck {
  match_status: 'found' | 'not_found' | 'check_failed';
  realms?: string[];
  fuzzy_matches?: Array<{ name: string; realm: string; asset_type: string; distance: number }>;
  error?: string;
}

function readSerialCheck(row: ManualReadingRecord): SerialCheck | null {
  const raw = row.source_payload;
  if (!raw) return null;
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : (raw as Record<string, unknown>);
    const sc = (obj as { serial_check?: SerialCheck }).serial_check;
    return sc ?? null;
  } catch {
    return null;
  }
}

// Friendly label for the stored values column on the row card.
function describeStoredValue(key: string, value: number): { label: string; hint?: string } {
  if (key === 'cumulative') {
    return { label: `Cumulative: ${value}`, hint: 'Will translate to a UtilSync Portal attribute at approval (electricity → Import_kWh kWh; water → waterLevel L = m³ × 1000)' };
  }
  return { label: `${key}: ${value}` };
}

interface PickerState {
  rowId: string;
  serial: string;
  matches: MeterSearchMatch[];
  values: Record<string, number>;
  /** The fingerprint of the held checks the reviewer confirmed, if any. */
  confirm?: string;
}

export default function PendingPage() {
  const { isAdmin } = useFieldRole();
  const [rows, setRows] = useState<ManualReadingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [picker, setPicker] = useState<PickerState | null>(null);
  const [lastApproveSummary, setLastApproveSummary] = useState<string | null>(null);
  // Map of `${realm}::${serial}` → existing pending_review commissioning row.
  // Populated from the pending response; used so sibling rows for the same
  // missing meter don't all spawn duplicate commissioning records on handoff.
  const [awaitingCommissioning, setAwaitingCommissioning] = useState<Record<string, string>>({});
  // Live existence map (serial → realms it lives in). Overrides the cached
  // serial_check in source_payload — once a meter gets commissioned, this
  // flips that row from "⚠ not found" back to ready-to-approve immediately.
  // `liveOk` reflects whether the live check actually ran; if Hyperdrive
  // failed we fall back to the cached check rather than misreporting every
  // serial as "not found".
  const [existingSerials, setExistingSerials] = useState<Record<string, string[]>>({});
  const [liveOk, setLiveOk] = useState(true);
  // Per-row selection set for bulk approve. We deliberately don't expose a
  // "select all" helper — the user wants curated picks, not a blanket sweep.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Reading checks per row (an overwrite, an out-of-order value, unusual
  // usage, or already recorded). The approve route re-runs and enforces them.
  const [checks, setChecks] = useState<Record<string, PendingReadingChecks>>({});
  const [checksNote, setChecksNote] = useState<string | null>(null);

  async function loadChecks() {
    try {
      const result = await api.getPendingReadingChecks();
      setChecks(result.data);
      setChecksNote(null);
    } catch {
      setChecks({});
      setChecksNote('The reading checks could not run just now. Approving runs them again.');
    }
  }

  function markHeld(rowId: string, opts: ApproveOpts | undefined, held: AttributeCheckResult[], digest?: string) {
    setChecks(prev => ({
      ...prev,
      [rowId]: {
        status: 'checked',
        asset_id: opts?.asset_id ?? '',
        realm: opts?.realm ?? '',
        asset_type: opts?.asset_type ?? '',
        already_recorded: false,
        held: true,
        attributes: held,
        checks_digest: digest,
      },
    }));
  }

  function toggleSelected(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getPendingManualReadings();
      setRows(result.data);
      // The checks carry meters' stored readings, so billing-api serves them
      // to field admins only, the same people who can approve.
      if (isAdmin) void loadChecks();
      setAwaitingCommissioning(result.awaiting_commissioning ?? {});
      setExistingSerials(result.existing_serials ?? {});
      setLiveOk(result.existing_serials_ok !== false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function approveWith(
    rowId: string,
    opts: ApproveOpts | undefined,
    confirm?: string,
  ) {
    setActingId(rowId);
    setError(null);
    try {
      const result = await api.approveManualReading(rowId, confirm ? { ...opts, confirm_checks: confirm } : opts);
      if (result.ok) {
        setRows(prev => prev.filter(r => r.id !== rowId));
        setPicker(null);
        if (result.already_recorded) {
          setLastApproveSummary('Already recorded: the same value is stored for that time, so nothing was written.');
        } else if (result.translation && result.translation.length > 0) {
          setLastApproveSummary(formatTranslationSteps(result.translation));
        } else {
          setLastApproveSummary('Reading committed.');
        }
      } else {
        setError(`Approve failed: ${result.or_response_summary || 'unknown error'}`);
      }
    } catch (err) {
      const held = heldChecks(err);
      if (held) {
        // The row stays; its notice and Confirm button now show why.
        setPicker(null);
        markHeld(rowId, opts, held.checks, held.digest);
      } else {
        setError((err as Error).message);
      }
    } finally {
      setActingId(null);
    }
  }

  async function handleApprove(row: ManualReadingRecord) {
    // A row its checks hold is approved only through its Confirm button, which
    // this same handler serves: the fingerprint of what the reviewer was shown
    // travels as confirm_checks, and the server holds the row again if the
    // checks have changed since (or another meter is picked).
    const confirm = confirmDigest(checks[row.id]);
    setError(null);
    setLastApproveSummary(null);

    // Cross-realm search to know the actual asset + asset_type, regardless
    // of whether row.asset_id was already set. We always search so the
    // approve API call carries asset_type for the translation step.
    setActingId(row.id);
    try {
      const result = await api.searchMetersBySerial(row.meter_serial);
      const matches = result.data;
      if (matches.length === 0) {
        setError(
          `No meter named '${row.meter_serial}' found in any realm. ` +
          'Commission the meter first, or reject this submission.',
        );
        setActingId(null);
        return;
      }

      // If the row already has an asset_id, prefer the matching match.
      // Otherwise, if there's only one match, auto-pick. Otherwise show picker.
      if (row.asset_id) {
        const m = matches.find(x => x.asset_id === row.asset_id);
        if (m) {
          await approveWith(row.id, { asset_id: m.asset_id, realm: m.realm, asset_type: m.asset_type }, confirm);
          return;
        }
        // Asset_id on row no longer exists in Portal — fall through to picker
      }

      if (matches.length === 1) {
        const m = matches[0];
        await approveWith(row.id, { asset_id: m.asset_id, realm: m.realm, asset_type: m.asset_type }, confirm);
        return;
      }
      setPicker({ rowId: row.id, serial: row.meter_serial, matches, values: readingsOf(row), confirm });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingId(null);
    }
  }

  async function handleReject(id: string) {
    const reason = window.prompt('Why are you rejecting this reading?', '');
    if (reason === null) return;
    setActingId(id);
    try {
      const result = await api.rejectManualReading(id, reason || undefined);
      if (result.ok) setRows(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingId(null);
    }
  }

  // "Hand off to commissioning" — admin reviewed the serial-check banner,
  // decided the meter genuinely doesn't exist (not just a typo), and wants
  // it routed into the commissioning queue. This ONLY triggers commissioning;
  // the reading itself stays in the queue and auto-resolves after the
  // commissioning row is finalized. Drop-on-purpose is via Reject.
  async function handleHandoff(row: ManualReadingRecord) {
    if (!window.confirm(
      `Treat ${row.meter_serial} as a new meter and route it to the commissioning queue?\n\n` +
      `Approve once the commissioning row is finalized — this reading and any siblings stay in the queue and resolve automatically.`
    )) return;
    setActingId(row.id);
    setError(null);
    try {
      const result = await api.handoffManualReadingToCommissioning(row.id);
      if (result.ok) {
        // Reading stays in the queue; flip the awaiting flag for this
        // (realm, serial) so the row (and any siblings) instantly switch
        // to the "awaiting commissioning" badge / muted button state.
        const key = `${row.realm}::${row.meter_serial}`;
        setAwaitingCommissioning(prev => ({ ...prev, [key]: result.commissioning_record_id }));
        const verb = result.deduped ? 'Already queued for' : 'Triggered';
        setLastApproveSummary(
          `${verb} commissioning of ${row.meter_serial} (record ${result.commissioning_record_id}). ` +
          `This reading and any siblings will resolve once the commissioning row is finalized.`
        );
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setActingId(null);
    }
  }

  // Bulk reject: useful while we're ironing out kinks in the email/OCR/xlsx
  // pipelines and the queue fills with test rows. One shared reason for the
  // whole batch; each row is rejected one at a time so partial failure is
  // visible (rows that succeeded disappear, the rest stay).
  async function handleRejectAll() {
    const ids = rows.map(r => r.id);
    if (ids.length === 0) return;
    if (!window.confirm(`Reject all ${ids.length} pending reading${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    const reason = window.prompt('Reason for rejecting all (optional):', 'bulk cleanup while testing') ?? '';
    setActingId('__bulk__');
    setError(null);
    let failed = 0;
    for (const id of ids) {
      try {
        const result = await api.rejectManualReading(id, reason || undefined);
        if (result.ok) {
          setRows(prev => prev.filter(r => r.id !== id));
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    setActingId(null);
    if (failed > 0) {
      setError(`Bulk reject finished with ${failed} failure${failed === 1 ? '' : 's'} — they remain in the queue.`);
    } else {
      setLastApproveSummary(`Rejected ${ids.length} reading${ids.length === 1 ? '' : 's'}.`);
    }
  }

  // Bulk approve only the rows the admin has explicitly checked. For each
  // row we run the same search-by-serial step the single Approve does and
  // commit only when the lookup is unambiguous (single match, or row.asset_id
  // still resolves). Multi-match and 0-match rows are SKIPPED with a reason
  // — admins handle those individually so we don't suppress the picker UI.
  async function handleApproveChecked() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    if (!window.confirm(`Approve ${ids.length} checked reading${ids.length === 1 ? '' : 's'}?`)) return;
    setActingId('__bulk__');
    setError(null);
    setLastApproveSummary(null);

    let approved = 0;
    const skipped: Array<{ serial: string; reason: string }> = [];
    for (const id of ids) {
      const row = rows.find(r => r.id === id);
      if (!row) continue;
      try {
        const result = await api.searchMetersBySerial(row.meter_serial);
        const matches = result.data;
        if (matches.length === 0) {
          skipped.push({ serial: row.meter_serial, reason: 'not in Portal' });
          continue;
        }
        let pick = row.asset_id ? matches.find(x => x.asset_id === row.asset_id) : undefined;
        if (!pick && matches.length === 1) pick = matches[0];
        if (!pick) {
          skipped.push({ serial: row.meter_serial, reason: `${matches.length} realms — needs manual pick` });
          continue;
        }
        const pickOpts = { asset_id: pick.asset_id, realm: pick.realm, asset_type: pick.asset_type };
        let apr: Awaited<ReturnType<typeof api.approveManualReading>>;
        try {
          apr = await api.approveManualReading(row.id, pickOpts);
        } catch (err) {
          const held = heldChecks(err);
          if (!held) throw err;
          // Never confirmed in bulk: the row stays, with its reasons shown.
          markHeld(row.id, pickOpts, held.checks, held.digest);
          skipped.push({ serial: row.meter_serial, reason: held.message });
          continue;
        }
        if (apr.ok) {
          approved++;
          setRows(prev => prev.filter(r => r.id !== id));
          setSelected(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        } else {
          skipped.push({ serial: row.meter_serial, reason: apr.or_response_summary || 'approve failed' });
        }
      } catch (err) {
        skipped.push({ serial: row.meter_serial, reason: (err as Error).message });
      }
    }
    setActingId(null);

    if (approved > 0 && skipped.length === 0) {
      setLastApproveSummary(`Approved ${approved} reading${approved === 1 ? '' : 's'}.`);
    } else if (skipped.length > 0) {
      const skipSummary = skipped
        .slice(0, 5)
        .map(s => `${s.serial} (${s.reason})`)
        .join('; ');
      const more = skipped.length > 5 ? ` …and ${skipped.length - 5} more` : '';
      setError(
        `Approved ${approved} of ${ids.length}. Skipped — handle individually: ${skipSummary}${more}`,
      );
    }
  }

  if (loading) {
    return <div className="bg-surface-raised rounded-xl shadow p-6 text-content-muted">Loading queue...</div>;
  }

  return (
    // Not wrapped in <Card>: bg-white/rounded-xl/shadow with no border-gray-*
    // — doesn't match the Card-adoption trigger, and Card's own border would
    // be a light-mode appearance change. Token-substituted in place, same as
    // MySubmissionsPage.tsx.
    <div className="bg-surface-raised rounded-xl shadow overflow-hidden">
      {/* Stacks below sm: as one flex row the buttons squeezed the title into
          a sliver at 360px. sm+ is the layout confirmed on desktop. */}
      <div className="px-6 py-4 border-b border-border-token flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-content">Pending approvals</h2>
          <p className="text-sm text-content-muted mt-0.5">
            {isAdmin
              ? 'Review and approve readings submitted by field:reader users.'
              : 'Read-only view of the approval queue. Only admins can approve or reject.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={load}
            disabled={actingId === '__bulk__'}
            className="text-sm px-3 py-1.5 bg-surface-sunken rounded hover:bg-border-token disabled:opacity-50 whitespace-nowrap"
          >
            Refresh
          </button>
          {isAdmin && selected.size > 0 && (
            <button
              onClick={handleApproveChecked}
              disabled={actingId === '__bulk__'}
              className="text-sm px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 whitespace-nowrap"
              title="Approve only the rows you've checked. Multi-match rows are skipped — handle them individually."
            >
              {actingId === '__bulk__' ? 'Approving…' : `Approve checked (${selected.size})`}
            </button>
          )}
          {isAdmin && rows.length > 0 && (
            <button
              onClick={handleRejectAll}
              disabled={actingId === '__bulk__'}
              // bg-red-50 is a fixed light fill, not a token — left as-is it
              // renders as a glaring light patch on the now-dark header row.
              // dark:bg-red-950/dark:border-red-800/dark:text-red-300 (8.51:1
              // against red-950, computed by hand) keeps the "destructive
              // outline button" reading in dark.
              className="text-sm px-3 py-1.5 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded hover:bg-red-100 dark:hover:bg-red-900 disabled:opacity-50 whitespace-nowrap"
              title="Reject every pending reading in the queue (useful while testing)"
            >
              {actingId === '__bulk__' ? `Rejecting…` : `Reject all (${rows.length})`}
            </button>
          )}
        </div>
      </div>

      {error && (
        // bg-red-50/text-red-800 is a fixed literal pair, invisible-on-dark
        // if left alone. dark:bg-red-900/dark:border-red-800/dark:text-red-100
        // (8.20:1, computed by hand) — same pattern as group A/D's severity
        // banners. This is an error message; it must stay legible.
        <div className="px-6 py-3 bg-red-50 dark:bg-red-900 border-b border-red-200 dark:border-red-800 text-sm text-red-800 dark:text-red-100">
          {error}
        </div>
      )}

      {checksNote && (
        <div className="px-6 py-3 bg-blue-50 dark:bg-blue-900 border-b border-blue-200 dark:border-blue-800 text-sm text-blue-800 dark:text-blue-100">
          {checksNote}
        </div>
      )}

      {lastApproveSummary && (
        <div className="px-6 py-3 bg-green-50 dark:bg-green-900 border-b border-green-200 dark:border-green-800 text-sm text-green-900 dark:text-green-100 flex items-start justify-between gap-4">
          <span>{lastApproveSummary}</span>
          <button
            onClick={() => setLastApproveSummary(null)}
            className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100 font-bold"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {picker && (
        <MeterPicker
          serial={picker.serial}
          matches={picker.matches}
          values={picker.values}
          onCancel={() => setPicker(null)}
          onPick={(m) => approveWith(picker.rowId, { asset_id: m.asset_id, realm: m.realm, asset_type: m.asset_type }, picker.confirm)}
          busy={actingId === picker.rowId}
        />
      )}

      {rows.length === 0 ? (
        <div className="px-6 py-12 text-center text-content-muted">
          No pending readings.
        </div>
      ) : (
        <ul className="divide-y divide-border-token">
          {rows.map(row => {
            const readings = readingsOf(row);
            const serialCheck = readSerialCheck(row);
            // Existence verdict: trust the live map when the worker's live
            // check ran (`liveOk`). Empty entry means "doesn't exist anywhere"
            // — authoritative. If live failed, fall back to the cached
            // queue-time `serial_check`, which may be stale in either
            // direction. Realm match: the meter has to exist in the row's
            // realm, otherwise we still surface a banner so admins don't
            // accidentally commit against the wrong tenant's meter.
            const liveRealms = existingSerials[row.meter_serial] ?? [];
            const liveExists = liveRealms.includes(row.realm);
            const otherRealmsOnly = !liveExists && liveRealms.length > 0;
            const cachedSaysNotFound = serialCheck?.match_status === 'not_found';
            const notFound = liveOk
              ? (!liveExists && (otherRealmsOnly || liveRealms.length === 0))
              : cachedSaysNotFound;
            const hasFuzzy = notFound && !otherRealmsOnly && (serialCheck?.fuzzy_matches?.length ?? 0) > 0;
            const awaitingKey = `${row.realm}::${row.meter_serial}`;
            const awaitingId = !liveExists ? awaitingCommissioning[awaitingKey] : undefined;
            return (
              // Row highlight is a colour-only state signal (awaiting vs
              // not-found vs normal) — bg-indigo-50/40 and bg-amber-50/40
              // are fixed literals that would wash out on the now-dark Card
              // row. dark:bg-*-950/40 keeps the tint distinguishable in dark.
              <li key={row.id} className={`px-6 py-4 ${awaitingId ? 'bg-indigo-50/40 dark:bg-indigo-950/40' : notFound ? 'bg-amber-50/40 dark:bg-amber-950/40' : ''}`}>
                {/* Below sm the action column wraps onto its own line under the
                    details (it is w-full there): beside them it left the
                    details ~145px at 360px. sm+ is the confirmed desktop row. */}
                <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-x-4 gap-y-3">
                  {isAdmin && (
                    <input
                      type="checkbox"
                      checked={selected.has(row.id)}
                      onChange={() => toggleSelected(row.id)}
                      disabled={actingId === '__bulk__'}
                      className="mt-0.5 shrink-0 h-4 w-4 rounded border-border-token text-accent focus:ring-primary-500"
                      aria-label={`Select reading ${row.meter_serial}`}
                      title="Select for bulk approve"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-content">
                      {row.meter_serial}{' '}
                      <span className="text-xs font-normal text-content-muted">
                        ({row.source})
                      </span>
                      {awaitingId ? (
                        // No Badge tone maps to indigo — routed to 'purple'
                        // (indigo's nearest available hue), same cross-group
                        // normalization groups D/E used for their own indigo
                        // chips.
                        <Badge tone="purple" wrap className="ml-2 font-normal">
                          ⏳ awaiting commissioning
                        </Badge>
                      ) : notFound && (
                        <Badge tone="warning" wrap className="ml-2 font-normal">
                          ⚠ not in UtilSync Portal
                        </Badge>
                      )}
                      {!row.asset_id && !notFound && !awaitingId && (
                        // 'info' rather than 'warning': this is a neutral
                        // "will resolve later" note, not a problem — using a
                        // distinct tone from the notFound chip above also
                        // avoids two different meanings rendering identically
                        // (both were amber/yellow — visually indistinguishable
                        // before this migration too, just never routed
                        // through a shared token that would have surfaced it).
                        <Badge tone="info" wrap className="ml-2 font-normal">
                          asset unresolved — will look up at approval
                        </Badge>
                      )}
                    </p>
                    <p className="text-xs text-content-muted mt-0.5">
                      Submitted {parseDbTimestamp(row.captured_at)?.toLocaleString() ?? row.captured_at} by {row.submitted_by.slice(0, 8)}…
                    </p>
                    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
                      {Object.entries(readings).map(([k, v]) => {
                        const desc = describeStoredValue(k, v);
                        return (
                          <span key={k} className="text-sm" title={desc.hint}>
                            <span className="font-mono text-content">{desc.label}</span>
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-xs text-content-muted mt-1">
                      Reading time: {parseDbTimestamp(row.reading_timestamp)?.toLocaleString() ?? row.reading_timestamp}
                    </p>
                    <ReadingChecksNotice result={checks[row.id]} />
                    {row.image_r2_key && (
                      <div className="mt-2">
                        <DownloadOriginalButton kind="reading" id={row.id} fallbackName={sourceFilename(row)} />
                      </div>
                    )}

                    {awaitingId ? (
                      // bg-indigo-50/text-indigo-900 is a fixed literal pair.
                      // dark:bg-indigo-900/dark:border-indigo-800/
                      // dark:text-indigo-100 (9.27:1, computed by hand).
                      <div className="mt-2.5 rounded-md border border-indigo-300 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900 px-3 py-2 text-xs text-indigo-900 dark:text-indigo-100">
                        <p className="font-medium">
                          Serial <code className="px-1 bg-indigo-100 dark:bg-indigo-800 rounded">{row.meter_serial}</code>{' '}
                          is already queued for commissioning (record <code className="text-[10px]">{awaitingId.slice(0, 8)}…</code>).
                        </p>
                        <p className="text-[11px] text-indigo-700 dark:text-indigo-300 mt-1">
                          Once an admin finalizes that commissioning row, come back and Approve this reading — the meter will resolve automatically.
                        </p>
                      </div>
                    ) : otherRealmsOnly ? (
                      <div className="mt-2.5 rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
                        <p className="font-medium">
                          Serial <code className="px-1 bg-amber-100 dark:bg-amber-800 rounded">{row.meter_serial}</code>{' '}
                          isn't in <code className="px-1 bg-amber-100 dark:bg-amber-800 rounded">{row.realm}</code>{' '}
                          — but it does exist in:{' '}
                          <span className="font-mono">{liveRealms.join(', ')}</span>.
                        </p>
                        <p className="text-[11px] text-amber-800 dark:text-amber-300 mt-1">
                          Looks like a realm mismatch on the inbound. Either correct the row's
                          realm at approval (use the picker — it's cross-realm), or hand off to
                          commissioning if this realm should also have its own meter with this serial.
                        </p>
                      </div>
                    ) : notFound && (
                      <div className="mt-2.5 rounded-md border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 px-3 py-2 text-xs text-amber-900 dark:text-amber-100">
                        <p className="font-medium">
                          Serial <code className="px-1 bg-amber-100 dark:bg-amber-800 rounded">{row.meter_serial}</code>{' '}
                          isn't in the UtilSync Portal.
                        </p>
                        {hasFuzzy ? (
                          <div className="mt-1.5 space-y-1">
                            <p className="text-amber-800 dark:text-amber-300">Possible typos — close matches in the Portal:</p>
                            <ul className="flex flex-wrap gap-1.5">
                              {serialCheck!.fuzzy_matches!.map((f, i) => (
                                <li
                                  key={i}
                                  // bg-white nested inside the now-dark amber-900
                                  // banner would float as a mismatched white
                                  // chip. dark:bg-amber-800 (one step lighter
                                  // than the banner's amber-900) keeps it read
                                  // as a distinct chip; dark:text-amber-100
                                  // (6.37:1, computed by hand) stays legible.
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white dark:bg-amber-800 border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-100 font-mono text-[11px]"
                                  title={`Levenshtein distance: ${f.distance}`}
                                >
                                  <span>{f.name}</span>
                                  <span className="text-amber-600 dark:text-amber-300 text-[10px]">{f.realm} · {f.asset_type === 'GroundwaterSensorAsset' ? 'water' : 'electricity'} · d={f.distance}</span>
                                </li>
                              ))}
                            </ul>
                            <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
                              Use Approve to search by serial — pick the right one in the modal. If none of these match, hand off to commissioning.
                            </p>
                          </div>
                        ) : (
                          <p className="text-[11px] text-amber-800 dark:text-amber-300 mt-1">
                            No close matches found — this looks like a brand-new meter. Hand off to commissioning if the field tech captured a real installation.
                          </p>
                        )}
                      </div>
                    )}
                    {serialCheck?.match_status === 'check_failed' && (
                      <p className="text-[11px] text-content-muted mt-2 italic">
                        (couldn't verify serial existence at queue time: {serialCheck.error?.slice(0, 80)})
                      </p>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="w-full sm:w-auto flex flex-row sm:flex-col justify-end gap-1.5 shrink-0">
                      <button
                        onClick={() => handleApprove(row)}
                        disabled={actingId === row.id}
                        // Held by its reading checks, the same button becomes
                        // the explicit override (handleApprove sends
                        // confirm_checks). bg-warning/text-warning-fg is the
                        // contrast-tested pair Button's warning variant uses.
                        className={`px-3 py-1.5 text-sm rounded disabled:opacity-50 ${
                          needsConfirmation(checks[row.id])
                            ? 'bg-warning text-warning-fg hover:opacity-90'
                            : 'bg-green-600 text-white hover:bg-green-700'
                        }`}
                      >
                        {actingId === row.id ? '...' : needsConfirmation(checks[row.id]) ? confirmLabel(checks[row.id]) : 'Approve'}
                      </button>
                      {notFound && !awaitingId && (
                        <button
                          onClick={() => handleHandoff(row)}
                          disabled={actingId === row.id}
                          // text-indigo-700 alone is 1.92:1 against the button's
                          // dark bg-surface-raised — fails badly.
                          // dark:text-indigo-400 (5.09:1, computed by hand).
                          className="px-3 py-1.5 text-sm bg-surface-raised border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-400 rounded hover:bg-indigo-50 dark:hover:bg-indigo-950 disabled:opacity-50"
                          title="Treat as a new meter and route to the commissioning queue. This reading stays in the queue."
                        >
                          Hand off
                        </button>
                      )}
                      <button
                        onClick={() => handleReject(row.id)}
                        disabled={actingId === row.id}
                        // Same Delete-trigger precedent used across groups A/D/E:
                        // dark:border-red-800 dark:text-red-400 (5.48:1)
                        // dark:hover:bg-red-950.
                        className="px-3 py-1.5 text-sm bg-surface-raised border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-950 disabled:opacity-50"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function formatTranslationSteps(steps: TranslationStep[]): string {
  return steps
    .map(s => {
      const valuePart = s.unit_note
        ? `${s.target_value} ${s.target_key} (${s.unit_note})`
        : `${s.target_value} → ${s.target_key}`;
      return `Wrote ${valuePart}`;
    })
    .join('. ');
}

function ParentBreadcrumb({ path }: { path: MeterSearchMatch['parent_path'] }) {
  if (!path || path.length === 0) {
    return <p className="text-xs text-content-muted mt-0.5 italic">no parent (top-level asset)</p>;
  }
  return (
    <p className="text-xs text-content-muted mt-0.5">
      {path.map((node, i) => (
        <span key={node.id}>
          {i > 0 && <span className="text-content-muted mx-1">›</span>}
          <span className="text-content">{node.name}</span>
          <span className="text-content-muted"> ({node.type})</span>
        </span>
      ))}
    </p>
  );
}

function MeterPicker({
  serial,
  matches,
  values,
  onCancel,
  onPick,
  busy,
}: {
  serial: string;
  matches: MeterSearchMatch[];
  values: Record<string, number>;
  onCancel: () => void;
  onPick: (m: MeterSearchMatch) => void;
  busy: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      {/* Modal-container precedent (Login/Tenants/Pockets/MeterConfigModal):
          fixed inset-0 overlay dialog, kept a plain bg-surface-raised div
          rather than <Card> so Card's own rounded-lg doesn't fight the
          intentional rounded-xl/shadow-xl combo. */}
      <div className="bg-surface-raised rounded-xl shadow-xl max-w-lg w-full">
        <div className="px-6 py-4 border-b border-border-token">
          <h3 className="font-semibold text-content">Pick a meter to commit to</h3>
          <p className="text-sm text-content-muted mt-1">
            Serial <code className="px-1.5 py-0.5 bg-border-token rounded">{serial}</code> matches{' '}
            {matches.length} asset{matches.length === 1 ? '' : 's'} in the UtilSync Portal.
          </p>
        </div>
        <ul className="divide-y divide-border-token max-h-96 overflow-y-auto">
          {matches.map(m => {
            // Compute what the Portal write will look like for this asset.
            const { steps } = translateForOr(values, m.asset_type);
            return (
              <li key={`${m.realm}:${m.asset_id}`} className="px-6 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-content">{m.asset_type}</p>
                    {/* Promoted to text-content (not text-content-muted): the
                        realm + asset id here are exactly the disambiguating
                        identifiers an admin uses to pick the right meter —
                        picking wrong misroutes a reading. */}
                    <p className="text-xs text-content mt-0.5">
                      realm <code className="bg-border-token px-1 rounded">{m.realm}</code>
                      {' '}— asset <code className="bg-border-token px-1 rounded font-mono text-[10px]">{m.asset_id}</code>
                    </p>
                    <ParentBreadcrumb path={m.parent_path} />
                    {steps.length > 0 ? (
                      <ul className="mt-1.5 space-y-0.5">
                        {steps.map((s, i) => (
                          // text-blue-800 alone is 1.74:1 against surface-raised
                          // dark — fails badly. dark:text-blue-300 = 8.41:1.
                          // This previews the exact value about to be written
                          // to the Portal — must stay readable.
                          <li key={i} className="text-xs text-blue-800 dark:text-blue-300">
                            → write <code className="bg-blue-50 dark:bg-blue-950 px-1 rounded font-mono">{s.target_value}</code> to <code className="bg-blue-50 dark:bg-blue-950 px-1 rounded">{s.target_key}</code>
                            {s.unit_note && <span className="text-content-muted"> ({s.unit_note})</span>}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      // text-yellow-700 is 3.08:1 against the card dark — fails.
                      // dark:text-yellow-300 = 11.51:1, computed by hand.
                      <p className="mt-1.5 text-xs text-yellow-700 dark:text-yellow-300">No translatable values — the write to UtilSync Portal will fail.</p>
                    )}
                  </div>
                  <button
                    onClick={() => onPick(m)}
                    disabled={busy}
                    className="px-3 py-1.5 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 shrink-0"
                  >
                    {busy ? '...' : 'Commit here'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
        <div className="px-6 py-3 border-t border-border-token flex justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-content-muted hover:text-content disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
